import { Divider } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IState } from '../../store/reducers'
import { LineageGraph } from '../../types/api'
import { PipelineJob } from '../../types/lineage'
import { PositionedNode } from '../../../libs/graph'
import { connect } from 'react-redux'
import { faProjectDiagram } from '@fortawesome/free-solid-svg-icons/faProjectDiagram'
import { formatUpdatedAt } from '../../helpers'
import { runStateColor } from '../../helpers/nodes'
import { theme } from '../../helpers/theme'
import { truncateText, truncateTextFront } from '../../helpers/text'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/system/Box'
import MQTooltip from '../../components/core/tooltip/MQTooltip'
import MqStatus from '../../components/core/status/MqStatus'
import MqText from '../../components/core/text/MqText'
import React from 'react'

interface StateProps {
  lineage: LineageGraph
}

export interface TableLineagePipelineNodeData {
  pipeline: PipelineJob
}

interface TableLineagePipelineNodeProps {
  node: PositionedNode<'pipeline', TableLineagePipelineNodeData>
}

const ICON_SIZE = 12

const TableLineagePipelineNode = ({ node }: TableLineagePipelineNodeProps & StateProps) => {
  const navigate = useNavigate()
  const { name, namespace } = useParams()
  const isSelected = name === node.data.pipeline.name && namespace === node.data.pipeline.namespace
  const handleClick = () => {
    navigate(
      `/lineage/job/${encodeURIComponent(node.data.pipeline.namespace)}/${encodeURIComponent(
        node.data.pipeline.name
      )}?tableLevelNode=${encodeURIComponent(node.id)}`
    )
  }

  const addToToolTip = (pipeline: PipelineJob) => {
    return (
      <foreignObject>
        <Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Namespace:
            </MqText>
            <MqText block font={'mono'}>
              {truncateTextFront(pipeline.namespace, 40)}
            </MqText>
          </Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Name:
            </MqText>
            <MqText block font={'mono'}>
              {truncateTextFront(pipeline.name, 40)}
            </MqText>
          </Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Tasks:
            </MqText>
            <MqText block font={'mono'}>
              {pipeline.taskCount}
            </MqText>
          </Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Pipeline Inputs:
            </MqText>
            <MqText block font={'mono'}>
              {pipeline.aggregatedInputs.length}
            </MqText>
          </Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Pipeline Outputs:
            </MqText>
            <MqText block font={'mono'}>
              {pipeline.aggregatedOutputs.length}
            </MqText>
          </Box>
          {pipeline.description && (
            <Box display={'flex'} justifyContent={'space-between'}>
              <MqText block bold sx={{ mr: 6 }}>
                Description:
              </MqText>
              <MqText block font={'mono'}>
                {pipeline.description}
              </MqText>
            </Box>
          )}
          <Box display={'flex'} justifyContent={'space-between'}>
            <MqText block bold sx={{ mr: 6 }}>
              Updated at:
            </MqText>
            <MqText block font={'mono'}>
              {formatUpdatedAt(pipeline.updatedAt)}
            </MqText>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <MqText block bold sx={{ mr: 6 }}>
              Latest Run:
            </MqText>
            <MqStatus
              label={pipeline.latestRun?.state || 'N/A'}
              color={
                pipeline.latestRun?.state
                  ? runStateColor(pipeline.latestRun?.state)
                  : theme.palette.secondary.main
              }
            />
          </Box>
        </Box>
      </foreignObject>
    )
  }

  return (
    <g>
      <Box
        component={'rect'}
        sx={{
          x: 0,
          y: 0,
          width: node.width,
          height: node.height,
          filter: isSelected ? `drop-shadow( 0 0 4px ${theme.palette.primary.main})` : 'none',
          rx: 4,
          fill: theme.palette.background.paper,
          cursor: 'pointer',
          transition: 'filter 0.3',
        }}
        onClick={handleClick}
      />
      <Box
        component={'rect'}
        x={0}
        y={0}
        height={node.height}
        width={24}
        sx={{
          rx: 4,
          fill: node.data.pipeline.latestRun
            ? runStateColor(node.data.pipeline.latestRun.state)
            : theme.palette.secondary.main,
        }}
      />
      <FontAwesomeIcon
        aria-hidden={'true'}
        title={'Pipeline'}
        icon={faProjectDiagram}
        width={ICON_SIZE}
        height={ICON_SIZE}
        x={6}
        y={ICON_SIZE / 2}
        color={theme.palette.common.white}
        onClick={handleClick}
      />
      <MQTooltip title={addToToolTip(node.data.pipeline)} placement={'right-start'}>
        <g>
          <text
            fontSize='8'
            fontFamily={`${'Source Code Pro'}, mono`}
            fill={'white'}
            x={28}
            y={10}
            onClick={handleClick}
            cursor={'pointer'}
          >
            PIPELINE
          </text>
          <text fontSize='8' fill={'white'} x={28} y={20} onClick={handleClick} cursor={'pointer'}>
            {truncateText(node.data.pipeline.name, 16)}
          </text>
          <text fontSize='6' fill={'#888'} x={28} y={28} onClick={handleClick} cursor={'pointer'}>
            {node.data.pipeline.taskCount} tasks
          </text>
        </g>
      </MQTooltip>
    </g>
  )
}

TableLineagePipelineNode.getLayoutOptions = (node: TableLineagePipelineNodeProps['node']) => ({
  ...node,
})

const mapStateToProps = (state: IState) => ({
  lineage: state.lineage.lineage,
})

export default connect(mapStateToProps)(TableLineagePipelineNode)